import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { schoolService } from '@/api/services/schoolService'
import { useAuthStore } from '@/store/useAuthStore'
import { cn, DUMMY_AVATAR, onAvatarError } from '@/shared/utils'
import { Spinner } from '@/shared/ui/Spinner'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { TrainerCard, mapSchoolTrainerRow } from '@/features/users/TrainerCard'
import {
  Building2, Mail, Phone, MapPin, FileText, BadgeCheck, Users, Package,
  Eye, Car, Percent,
} from 'lucide-react'

const enName = (arr: any[], key = 'name') =>
  arr?.find((tr: any) => tr?.locale?.toLowerCase() === 'en')?.[key] ?? arr?.[0]?.[key]

const TABS = [
  { value: 'details',  labelKey: 'mySchool.tabDetails' },
  { value: 'trainers', labelKey: 'mySchool.tabTrainers' },
  { value: 'packages', labelKey: 'mySchool.tabPackages' },
]

function Info({ icon, label, value }: { icon?: React.ReactNode; label: string; value?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 flex items-center gap-1.5 flex-shrink-0">{icon}{label}</span>
      <span className="text-xs font-semibold text-gray-900 text-end">{value ?? '—'}</span>
    </div>
  )
}

// ── Details tab ──────────────────────────────────────────────────────────────
function DetailsTab({ row, school, vendorId }: { row: any; school: any; vendorId?: number }) {
  const { t } = useTranslation()
  const name = enName(school?.vendor_translations) ?? '—'
  const about = enName(school?.vendor_translations, 'about')
  const addresses: any[] = school?.vendor_addresses ?? []

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* School info */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-brand-500 flex items-center gap-2"><Building2 size={15} className="text-white"/><span className="text-sm font-bold text-white">{t('mySchool.schoolInformation')}</span></div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-base font-bold text-gray-900">{name}</div>
            <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold capitalize',
              school?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>{school?.status ?? '—'}</span>
          </div>
          <Info icon={<Mail size={12}/>}   label={t('mySchool.email')}  value={school?.email}/>
          <Info icon={<Phone size={12}/>}  label={t('mySchool.phone')}  value={school?.phone_number}/>
          <Info icon={<FileText size={12}/>} label={t('mySchool.about')} value={about}/>
          <Info icon={<Percent size={12}/>} label={t('mySchool.certificateCommission')} value={school?.certificate_commission_in_percentage != null ? `${school.certificate_commission_in_percentage}%` : '—'}/>
          <Info icon={<Percent size={12}/>} label={t('mySchool.schoolCommission')} value={(school?.min_commision!=null||school?.max_commision!=null) ? `${school?.min_commision ?? 0}% – ${school?.max_commision ?? 0}%` : '—'}/>
          <Info label={t('mySchool.license')} value={school?.license_file
            ? <a href={school.license_file} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">{t('mySchool.viewLicense')}</a>
            : '—'}/>
        </div>
      </div>

      {/* Admin + addresses */}
      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-900 flex items-center gap-2"><BadgeCheck size={15} className="text-white"/><span className="text-sm font-bold text-white">{t('mySchool.schoolAdmin')}</span></div>
          <div className="p-5 flex items-center gap-3">
            <img src={row?.user?.photo_url || DUMMY_AVATAR} alt={row?.user?.name} onError={onAvatarError}
              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm bg-gray-100"/>
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate">{row?.user?.name ?? '—'}</div>
              <div className="text-xs text-gray-500 truncate">{row?.user?.email}</div>
              <div className="text-xs text-gray-400">+{row?.user?.country_code ?? '971'} {row?.user?.phone}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-green-500 flex items-center gap-2"><MapPin size={15} className="text-white"/><span className="text-sm font-bold text-white">{t('mySchool.addresses', { count: addresses.length })}</span></div>
          <div className="p-5 space-y-3">
            {addresses.length === 0 ? <p className="text-xs text-gray-400 text-center py-3">{t('mySchool.noAddresses')}</p> :
              addresses.map((a:any, i:number) => (
                <div key={a.id ?? i} className="flex items-start gap-2 text-xs text-gray-700 border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                  <MapPin size={13} className="text-gray-400 flex-shrink-0 mt-0.5"/>
                  <span>{[a.street_address, a.city ?? a.city_id, a.state ?? a.state_id, a.country].filter(Boolean).join(', ') || '—'}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {vendorId && <input type="hidden" value={vendorId}/>}
    </div>
  )
}

// ── Trainers tab — reuses the same TrainerCard as /school/trainers-list ──────
function TrainersTab({ navigate }: { navigate: (p: string) => void }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const { data, isLoading } = useQuery({
    queryKey: ['my-school-trainers', page],
    queryFn: () => schoolService.schoolTrainers({ page, limit: 15 }),
  })
  const rawRows: any[] = (data as any)?.data ?? []
  const rows = rawRows.map(mapSchoolTrainerRow)
  const total = (data as any)?.total ?? rows.length

  if (isLoading) return <div className="flex justify-center py-16"><Spinner/></div>
  if (rows.length === 0) return <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-sm text-gray-400">{t('mySchool.noTrainers')}</div>

  return (
    <>
      <p className="text-sm text-gray-500 mb-3">{t('mySchool.trainerCount', { count: total })}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((u:any) => (
          <TrainerCard
            key={u.id}
            user={u}
            isSchool
            onView={() => u.id && navigate(`/school/trainer/${u.id}`)}
          />
        ))}
      </div>
      {total > 15 && (
        <div className="flex justify-center gap-2 mt-5">
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40">{t('mySchool.prev')}</button>
          <span className="px-3 py-1.5 text-xs text-gray-500">{t('mySchool.pageLabel', { page })}</span>
          <button onClick={()=>setPage(p=>p+1)} disabled={page*15>=total} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40">{t('mySchool.next')}</button>
        </div>
      )}
    </>
  )
}

// ── Packages tab ─────────────────────────────────────────────────────────────
function PackagesTab({ schoolId, navigate }: { schoolId?: number; navigate: (p: string) => void }) {
  const { t } = useTranslation()
  const authSchoolId = (useAuthStore((s) => s.user) as any)?.school_id
  const sid = schoolId ?? authSchoolId
  const { data, isLoading } = useQuery({
    queryKey: ['my-school-packages', sid],
    queryFn: () => schoolService.packageList({ school_id: sid, limit: 100 }),
  })
  const rows: any[] = (data as any)?.data ?? []

  if (isLoading) return <div className="flex justify-center py-16"><Spinner/></div>
  if (rows.length === 0) return <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-sm text-gray-400">{t('mySchool.noPackages')}</div>

  const themeMap: Record<string,string> = { unlimited:'bg-purple-50 text-purple-700', gold:'bg-brand-50 text-brand-700', silver:'bg-gray-50 text-gray-600', bronze:'bg-orange-50 text-orange-700', trial:'bg-brand-50 text-brand-700', express:'bg-red-50 text-red-700', red:'bg-red-50 text-red-700' }

  return (
    <>
      <p className="text-sm text-gray-500 mb-3">{t('mySchool.packageCount', { count: rows.length })}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((p:any) => {
          const name = enName(p.package_translations) ?? '—'
          const unlimited = p.is_unlimited || p.number_of_sessions === -1
          return (
            <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-gray-900 truncate">{name}</div>
                  <span className={cn('inline-block mt-1 px-2 py-0.5 rounded-full text-[12px] font-bold capitalize', themeMap[p.background_color?.toLowerCase()] ?? 'bg-gray-50 text-gray-600')}>{p.background_color ?? t('mySchool.packageFallback')}</span>
                </div>
                <span className={cn('px-2 py-0.5 rounded-full text-[12px] font-bold flex-shrink-0', p.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>{p.is_published ? t('mySchool.published') : t('mySchool.draft')}</span>
              </div>
              <div className="px-4 pb-3 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-brand-50 border border-brand-100 p-2.5 text-center">
                  <DirhamAmount value={Math.round(Number(p.min_price ?? 0))} textClass="text-base font-black text-gray-900" size={12}/>
                  <div className="text-[11px] text-gray-400 font-semibold uppercase mt-0.5">{t('mySchool.fromPrice')}</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-2.5 text-center">
                  <div className="text-base font-black text-gray-900 flex items-center justify-center gap-1">{unlimited ? '∞' : p.number_of_sessions}</div>
                  <div className="text-[11px] text-gray-400 font-semibold uppercase mt-0.5">{t('mySchool.sessions')}</div>
                </div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-2 text-center flex items-center justify-center gap-1 text-xs font-semibold text-gray-700"><Car size={12} className="text-gray-400"/>{enName(p.category?.category_translations) ?? '—'}</div>
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-2 text-center text-xs font-semibold text-gray-700">{t('mySchool.citiesCount', { count: p.package_city?.length ?? 0 })}</div>
              </div>
              <div className="px-4 pb-4">
                <button onClick={() => navigate(`/school/package/${p.id}`)}
                  className="w-full py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50">
                  <Eye size={12}/> {t('mySchool.viewPackage')}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export function MySchoolPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)
  const [tab, setTab] = useState('details')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-vendor'],
    queryFn: () => schoolService.myVendor(),
  })

  // The response can arrive in several shapes:
  //  • paginator:        { data: { data: [ { user, vendor, ... } ], total, ... } }
  //  • bare array:       { data: [ { user, vendor, ... } ] }
  //  • single vendor:    { data: { ...vendor } }
  // Unwrap to the first wrapper row regardless.
  const body: any = (data as any)?.data ?? data ?? {}
  const row: any = Array.isArray(body)
    ? (body[0] ?? {})
    : Array.isArray(body?.data)
      ? (body.data[0] ?? {})
      : body
  // myVendor may return the vendor nested (row.school / row.vendor) or at the root (row itself).
  const school = row.school ?? row.vendor ?? row
  // Resolve the vendor id from any of those shapes, finally falling back to the logged-in admin's school_id.
  const vendorId = row.vendor_id ?? row.vendor?.id ?? row.school?.id ?? school?.id ?? row.id ?? (currentUser as any)?.school_id
  const schoolName = enName(school?.vendor_translations) ?? t('mySchool.defaultName')

  if (isLoading) return <div className="flex justify-center items-center h-64"><Spinner className="w-8 h-8"/></div>
  if (isError)   return <div className="text-center py-20 text-sm text-red-500">{t('mySchool.failedToLoad')}</div>

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
          <span>{t('mySchool.breadcrumbDashboard')}</span><span>/</span><span className="text-brand-500 font-semibold">{t('mySchool.breadcrumbTitle')}</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Building2 size={20} className="text-brand-500"/>{schoolName}</h1>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-3 py-1 flex items-center gap-1">
        {TABS.map(tb => (
          <button key={tb.value} onClick={()=>setTab(tb.value)}
            className={cn('px-4 py-2.5 text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5',
              tab===tb.value ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-500 hover:text-brand-600 hover:bg-brand-50')}>
            {tb.value==='details' && <Building2 size={13}/>}
            {tb.value==='trainers' && <Users size={13}/>}
            {tb.value==='packages' && <Package size={13}/>}
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {tab === 'details'  && <DetailsTab row={row} school={school} vendorId={vendorId}/>}
      {tab === 'trainers' && <TrainersTab navigate={navigate}/>}
      {tab === 'packages' && <PackagesTab schoolId={vendorId} navigate={navigate}/>}
    </div>
  )
}
